import { Card as AntCard, CardProps as AntCardProps } from 'antd'
import { ReactNode } from 'react'

export interface CardProps extends AntCardProps {
  /**
   * Show hover effect
   */
  hoverable?: boolean

  /**
   * Remove default padding
   */
  noPadding?: boolean

  /**
   * Card title
   */
  title?: ReactNode

  /**
   * Card extra content (appears in the top-right)
   */
  extra?: ReactNode

  /**
   * Card content
   */
  children: ReactNode

  /**
   * Card bordered
   * @default true
   */
  bordered?: boolean

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Card Component
 *
 * A container component for grouping related content.
 *
 * @example
 * <Card title="User Details">
 *   <p>Name: John Doe</p>
 *   <p>Email: john@example.com</p>
 * </Card>
 *
 * @example
 * // Card with extra actions
 * <Card
 *   title="Statistics"
 *   extra={<Button variant="text">View More</Button>}
 * >
 *   <p>Total Users: 1,234</p>
 * </Card>
 *
 * @example
 * // Hoverable card
 * <Card hoverable onClick={handleClick}>
 *   Click me
 * </Card>
 */
export function Card({
  hoverable = false,
  noPadding = false,
  children,
  bordered = true,
  className,
  style,
  ...props
}: CardProps) {
  // Merge styles, with custom style taking precedence
  const mergedStyle = {
    ...(bordered ? { border: '1px solid #e5e7eb' } : {}),
    ...style,
  }

  return (
    <AntCard
      hoverable={hoverable}
      variant={bordered ? 'bordered' : 'borderless'}
      styles={noPadding ? { body: { padding: 0 } } : undefined}
      className={className}
      {...props}
      style={mergedStyle}
    >
      {children}
    </AntCard>
  )
}
